import uniq from 'lodash/uniq';
import uniqBy from 'lodash/uniqBy';
import configuration from '@assets/global_config';
import { getOS } from '@core/userAgent';
import { AppContextType } from '@embed/components/helpers';
import { PlayTriggersEnum } from '@embed/constants';
import { customEmbedEvent } from '@embed/customEmbedEvent';
import { appContextToTrackingData, appContextToTrackingDataPixel, extractPageUrl, nowAndNowLocalToString, } from '@embed/helpers';
import { sendPixelTrackingWithSession, sendTrackingWithSession, } from './tools/tracking';
import { containerPlacement, filterCurrentInViewport, pruneNilValues, sortByOtherArray, } from './tools/utils';
const { lastActivitySetter, tracking } = configuration;
const { urls: trackingUrls, pixelUrls: pixelTrackingUrls } = tracking || {};
const resolveNextAutoplay = ({ state }) => {
    const { inViewportIds, navigation, videos } = state;
    const viewportIds = sortByOtherArray(filterCurrentInViewport(inViewportIds), navigation);
    const ads = viewportIds.filter((encoded_id) => videos[encoded_id].vast_tag);
    const featured = viewportIds.filter((encoded_id) => videos[encoded_id].badge);
    return ads.length > 0
        ? ads[0]
        : featured.length > 0
            ? featured[0]
            : viewportIds[0];
};
export const initialState = {
    appContext: null,
    experiments: {},
    // experimentsFetched: false, // Flag for finished fetching the experiements from the API
    largeCarousel: false,
    inViewportIdsReportQueue: [],
    inViewportIdsReportSuccess: [],
    inViewportIds: {},
    autoplayIds: {},
    autoplayIdsPrevious: {},
    thumbnailIdsLoaded: {},
    thumbnailImpressionVideoReportQueue: [],
    thumbnailImpressionIdsReportSuccess: {},
    embedImpressionTracked: false,
    playTrigger: {},
    engagement: {
        // :nav, // "first", "next", "prev", "push", "pop"
        // :context,
        // :video_type,
        seconds_watched: 0,
        os: getOS(),
        app_version: process.env.PRODUCT_VERSION,
        platform: process.env.PLATFORM,
        product: process.env.PRODUCT,
    },
};
export const actions = () => ({
    processInViewportQueue: (state) => {
        const { params, appContext, navigation, inViewportIdsReportQueue, inViewportIdsReportSuccess, } = state;
        const alreadySent = new Set(inViewportIdsReportSuccess);
        const ids = [...new Set(inViewportIdsReportQueue)].filter((x) => !alreadySent.has(x));
        if (ids.length) {
            const viewportVideoIds = sortByOtherArray(ids, navigation);
            // Send Naboo tracking
            const data = Object.assign({ viewport_video_ids: viewportVideoIds }, appContextToTrackingData({ appContext }));
            sendTrackingWithSession({
                url: trackingUrls({}).scrollVideos,
                data,
                host: params.api_host_tracking_legacy,
            });
            // Send Pixel tracking
            const contextDataPixel = appContextToTrackingDataPixel({ appContext });
            const pixelData = {
                event_properties: pruneNilValues(Object.assign(Object.assign({}, contextDataPixel), { _viewport_videos_ids: viewportVideoIds })),
                _publisher_client_id: contextDataPixel._publisher_client_id,
                publisher_client_source: extractPageUrl({ params }),
            };
            sendPixelTrackingWithSession({
                url: pixelTrackingUrls().feedScrollVideos,
                data: pixelData,
                host: params.api_host_tracking,
            });
            return {
                inViewportIdsReportQueue: [],
                inViewportIdsReportSuccess: [...alreadySent, ...ids],
            };
        }
    },
    setContext: (state, action) => {
        const { appContext } = action;
        // Resets engagement tracking state when the context changes
        return {
            appContext,
            engagement: {},
        };
    },
    trackScrollEndVideos: (state) => {
        const { params, appContext, navigation, inViewportIds } = state;
        const ids = filterCurrentInViewport(inViewportIds);
        if (ids.length) {
            const viewportVideoIds = sortByOtherArray(ids, navigation);
            // Send Naboo tracking
            const data = Object.assign({ viewport_video_ids: viewportVideoIds }, appContextToTrackingData({ appContext }));
            sendTrackingWithSession({
                url: trackingUrls({}).scrollEndVideos,
                data,
                host: params.api_host_tracking_legacy,
            });
            // Send Pixel tracking
            const contextDataPixel = appContextToTrackingDataPixel({ appContext });
            const pixelData = {
                event_properties: pruneNilValues(Object.assign(Object.assign({}, contextDataPixel), { _viewport_videos_ids: viewportVideoIds })),
                _publisher_client_id: contextDataPixel._publisher_client_id,
                publisher_client_source: extractPageUrl({ params }),
            };
            sendPixelTrackingWithSession({
                url: pixelTrackingUrls().feedScrollEndVideos,
                data: pixelData,
                host: params.api_host_tracking,
            });
        }
        return {};
    },
    trackEmbedImpression: (state, action) => {
        const { embedImpressionTracked, params, videos } = state;
        const { container, eventType, eventData } = action;
        const appContext = action.appContext || state.appContext;
        if (!embedImpressionTracked) {
            const [now, nowLocal] = nowAndNowLocalToString();
            const { mode, placement } = params;
            // Send Naboo tracking
            const videosArray = Object.values(videos);
            const data = pruneNilValues(Object.assign(Object.assign({ displayed_at: now, displayed_at_local: nowLocal, mode: mode || '', page_url: extractPageUrl({ params }), placement: placement || '', variant: videosArray.length > 0 ? videosArray[0].variant : '' }, (container ? containerPlacement(container) : {})), appContextToTrackingData({ appContext })));
            sendTrackingWithSession({
                url: '/embed/impressions',
                data,
                host: params.api_host_tracking_legacy,
            });
            // Send Pixel tracking
            if (appContext.appContextType !== // TODO find way how to unify with floating button
                AppContextType.EMBED_CHANNEL_BUTTON_CONTEXT_TYPE) {
                const contextDataPixel = appContextToTrackingDataPixel({ appContext });
                const pixelData = {
                    event_properties: pruneNilValues(Object.assign({ autoplay: params.autoplay, placement }, contextDataPixel)),
                    _publisher_client_id: contextDataPixel._publisher_client_id,
                    publisher_client_source: extractPageUrl({ params }),
                };
                sendPixelTrackingWithSession({
                    url: pixelTrackingUrls().feedCreateFeedImpression,
                    data: pixelData,
                    host: params.api_host_tracking,
                });
                eventType &&
                    customEmbedEvent({
                        type: eventType,
                        params,
                        data: eventData || {},
                    });
                return {
                    embedImpressionTracked: true,
                };
            }
        }
    },
    trackVideoPick: (state, action) => {
        const { video } = action;
        const { variant } = video;
        const { params, appContext, inViewportIds, navigation } = state;
        const playTrigger = state.playTrigger[video.encoded_id] || PlayTriggersEnum.PLAY_TRIGGER_CLICK;
        const contextData = appContextToTrackingData({ appContext });
        const viewportVideoIds = sortByOtherArray(uniq([...filterCurrentInViewport(inViewportIds), video.encoded_id]), navigation);
        lastActivitySetter(params);
        // Send Naboo tracking
        const data = pruneNilValues(Object.assign({ play_trigger: playTrigger, variant: variant, viewport_video_ids: viewportVideoIds }, contextData));
        sendTrackingWithSession({
            url: trackingUrls({ videoId: video.encoded_id }).picked_video,
            data,
            host: params.api_host_tracking_legacy,
        });
        // Send Pixel tracking
        const contextDataPixel = appContextToTrackingDataPixel({
            appContext,
        });
        const pixelData = {
            event_properties: pruneNilValues(Object.assign({ ad_id: video.ad_id, autoplay: params.autoplay, canonical_url: video.canonical_url, play_trigger: playTrigger, variant: video.variant, _video_id: video.encoded_id, _viewport_video_ids: viewportVideoIds }, contextDataPixel)),
            _publisher_client_id: contextDataPixel._publisher_client_id,
            publisher_client_source: extractPageUrl({ params }),
        };
        sendPixelTrackingWithSession({
            url: pixelTrackingUrls().feedClickVideo,
            data: pixelData,
            host: params.api_host_tracking,
        });
        return {};
    },
    registerInViewport: (state, action) => {
        const { video: { encoded_id }, } = action;
        const { inViewportIds, inViewportIdsReportQueue } = state;
        // There might be a IntersectionObserver bug in UC Browser (More than 16% of India users)
        // where isIntersecting is always undefined
        // Solution might be to check float intersectionRatio instead of boolean isIntersecting
        if (!inViewportIds[encoded_id]) {
            return {
                inViewportIdsReportQueue: [...inViewportIdsReportQueue, encoded_id],
                inViewportIds: Object.assign(Object.assign({}, inViewportIds), { [encoded_id]: true }),
            };
        }
    },
    unregisterInViewport: (state, action) => {
        const { video: { encoded_id }, } = action;
        const { inViewportIds } = state;
        if (inViewportIds[encoded_id]) {
            return {
                inViewportIds: Object.assign(Object.assign({}, inViewportIds), { [encoded_id]: false }),
            };
        }
    },
    setPlayTrigger: (state, action) => {
        const { playTrigger } = state;
        const { video, type } = action;
        return {
            playTrigger: Object.assign(Object.assign({}, playTrigger), { [video.encoded_id]: type }),
        };
    },
    // Handles manually triggered autoplay, e.g. hoverplay
    setAutoplay: (state, action) => {
        const { video } = action || {};
        const { autoplayIds, iframe, player, inViewportIds } = state;
        if ((iframe === null || iframe === void 0 ? void 0 : iframe.isOpen) || (player === null || player === void 0 ? void 0 : player.isOpen) || !video || !video.encoded_id) {
            return state;
        }
        // video is in viewport and has no autoplay
        if (inViewportIds[video.encoded_id] && !autoplayIds[video.encoded_id]) {
            return {
                autoplayIds: {
                    [video.encoded_id]: true,
                },
            };
        }
    },
    // Handles automatic autoplay
    registerForAutoplay: (state, action) => {
        const { video } = action || {};
        const { autoplayIds, playTrigger, iframe, player, params } = state;
        const autoplayEnabled = params.autoplay && !(iframe === null || iframe === void 0 ? void 0 : iframe.isOpen) && !(player === null || player === void 0 ? void 0 : player.isOpen);
        if (!autoplayEnabled) {
            return state;
        }
        const nextAutoplayId = (video && video.encoded_id) || resolveNextAutoplay({ state });
        if (!autoplayIds[nextAutoplayId]) {
            return {
                playTrigger: Object.assign(Object.assign({}, playTrigger), { [nextAutoplayId]: PlayTriggersEnum.PLAY_TRIGGER_AUTOPLAY }),
                autoplayIds: {
                    [nextAutoplayId]: true,
                },
            };
        }
    },
    unregisterForAutoplay: (state, action) => {
        const { video } = action;
        const { autoplayIds, iframe, player, params } = state;
        const autoplayEnabled = params.autoplay && !(iframe === null || iframe === void 0 ? void 0 : iframe.isOpen) && !(player === null || player === void 0 ? void 0 : player.isOpen);
        if (!autoplayEnabled) {
            return state;
        }
        if (autoplayIds[video.encoded_id]) {
            return {
                autoplayIds: Object.assign(Object.assign({}, autoplayIds), { [video.encoded_id]: false }),
            };
        }
    },
    setThumbnailLoaded: (state, action) => {
        const { thumbnailIdsLoaded: loaded } = state;
        const { video: { encoded_id }, } = action;
        if (!loaded[encoded_id]) {
            return {
                thumbnailIdsLoaded: Object.assign(Object.assign({}, loaded), { [encoded_id]: true }),
            };
        }
    },
    registerThumbnailImpression: (state, action) => {
        const { thumbnailIdsLoaded: loaded, thumbnailImpressionVideoReportQueue: queue, thumbnailImpressionIdsReportSuccess: success, } = state;
        const { video } = action;
        if (loaded[video.encoded_id] && !success[video.encoded_id]) {
            return {
                thumbnailImpressionVideoReportQueue: [...queue, video],
            };
        }
    },
    processThumbnailImpressionQueue: (state) => {
        const { params, appContext, thumbnailImpressionVideoReportQueue: queue, thumbnailImpressionIdsReportSuccess: success, } = state;
        const { autoplay, mode, placement } = params;
        const videos = uniqBy(queue, (video) => video.encoded_id);
        if (videos.length) {
            const [now, nowLocal] = nowAndNowLocalToString();
            // Send Naboo tracking
            const data = Object.assign({ displayed_at: now, displayed_at_local: nowLocal, mode: mode, page_url: extractPageUrl({ params }), placement: placement || '', variant: videos[0].variant, video_ids: videos.map((video) => video.encoded_id) }, appContextToTrackingData({ appContext }));
            sendTrackingWithSession({
                url: trackingUrls({}).thumbnailImpressions,
                data,
                host: params.api_host_tracking_legacy,
            });
            // Send Pixel tracking
            const contextDataPixel = appContextToTrackingDataPixel({ appContext });
            const pixelData = {
                event_properties: pruneNilValues(Object.assign({ autoplay,
                    placement, videos: videos.map((video) => ({
                        _video_id: video.encoded_id,
                        variant: video.variant,
                    })) }, contextDataPixel)),
                _publisher_client_id: contextDataPixel._publisher_client_id,
                publisher_client_source: extractPageUrl({ params }),
            };
            sendPixelTrackingWithSession({
                url: pixelTrackingUrls().feedCreateThumbnailImpression,
                data: pixelData,
                host: params.api_host_tracking,
            });
            const newSuccess = videos.reduce((acc, video) => {
                return Object.assign(Object.assign({}, acc), { [video.encoded_id]: true });
            }, {});
            return {
                thumbnailImpressionVideoReportQueue: [],
                thumbnailImpressionIdsReportSuccess: Object.assign(Object.assign({}, success), newSuccess),
            };
        }
    },
});
